import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { UiPillStatusEnum } from 'src/app/shared/enums/ui-pill-status.enum';
import { ProductStatus } from 'src/app/storage-user/enums/product-status.enum';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';

import { TranslateService } from '@ngx-translate/core';
import { ModuleName } from 'src/app/shared/enums/enums';
import { IStock } from 'src/app/shared/models/stock.model';
import { AddToStockModalComponent } from 'src/app/shared/stock-module/add-to-stock-modal/add-to-stock-modal.component';
import { UseStockModalComponent } from 'src/app/shared/stock-module/use-stock-modal/use-stock-modal.component';
import { ProductAction } from 'src/app/storage-user/enums/product-action.enum';
import { createProductTransferDialogConfig } from 'src/app/storage-user/functions/create-product-transfer-dialog-config';
import {
  TransferProductModalComponent,
  TransferProductModalData,
} from 'src/app/storage-user/modals/transfer-product-modal/transfer-product-modal.component';
import { mapStock } from 'src/app/storage-user/models/use-product.model';
import { ITransferProductRequest } from 'src/app/storage-user/models/product.model';
import {
  AddToStock,
  TransferProduct,
  UseProduct,
} from 'src/app/storage-user/state/product.actions';
import { AddToCart, RemoveFromCart } from 'src/app/storage-user/state/cart.actions';
import { CartState } from 'src/app/storage-user/state/cart.state';
import { Privileges } from 'src/app/core/privileges/privileges';
import { CartAnimationService } from 'src/app/storage-user/services/cart-animation.service';

@Component({
  selector: 'app-storage-template',
  templateUrl: './storage-template.component.html',
  styleUrls: ['./storage-template.component.scss'],
  standalone: false,
})
export class StorageTemplateComponent implements OnInit {
  @Input() columnName: string;
  @Input() element: any;

  Privileges = Privileges;
  allDepsToken = ALL_DEPS_TOKEN;
  hasMultipleDepartments = select(TenantUserAccessState.hasMultipleDepartments);
  currentDepartmentId = select(TenantUserAccessState.currentDepartmentId);
  isInCart = select(CartState.isInCart);
  transferDepartmentLabel = '';

  addToStockActions = [AddToStock];
  useActions = [UseProduct];
  matchesRow = (action: AddToStock | UseProduct): boolean =>
    action.product?.storageProductId === this.element?.id;

  private statusMapper = {
    [ProductStatus.Green]: UiPillStatusEnum.Ok,
    [ProductStatus.Yellow]: UiPillStatusEnum.UnderThreshold,
    [ProductStatus.Red]: UiPillStatusEnum.UnderMinimum,
    [ProductStatus.Blue]: UiPillStatusEnum.ProductInOrder,
  };

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private translate: TranslateService,
    private cartAnimation: CartAnimationService
  ) {}

  get statusColor(): string {
    return this.statusMapper[this.element.status as ProductStatus];
  }

  ngOnInit(): void {
    if (
      this.element.storageProductAction === ProductAction.TransferFromStorage &&
      this.element.destinationDepartment
    ) {
      this.transferDepartmentLabel = `${this.translate.instant('to').toLowerCase()} ${
        this.element.destinationDepartment.name
      }`;
    }
    if (
      this.element.storageProductAction === ProductAction.TransferToStorage &&
      this.element.sourceDepartment
    ) {
      this.transferDepartmentLabel = `${this.translate.instant('from').toLowerCase()} ${
        this.element.sourceDepartment.name
      }`;
    }
  }

  use(): void {
    this.dialog
      .open(UseStockModalComponent, {
        width: this.element.isChemical ? '52rem' : '30rem',
        panelClass: 'mobile-sizing-for-mat-dialogs',
        data: {
          id: `${this.element.id}`,
          departmentId: this.element.department?.id || this.element.departmentId,
          currentStock: `${this.element.quantity}`,
          module: ModuleName.STORAGE,
          isChemical: !!this.element.isChemical,
          substanceName: this.element.name,
          manufacturerName: this.element.maker,
        },
      })
      .afterClosed()
      .subscribe((result: IStock) => {
        if (result) {
          const useProduct = mapStock(result);
          this.store.dispatch(new UseProduct(useProduct));
        }
      });
  }

  addToStock(): void {
    this.dialog
      .open(AddToStockModalComponent, {
        width: '45rem',
        panelClass: 'mobile-sizing-for-mat-dialogs',
        data: {
          id: `${this.element.id}`,
          currentStock: `${this.element.quantity}`,
        },
      })
      .afterClosed()
      .subscribe((result: IStock) => {
        if (result) {
          const useProduct = mapStock(result);
          this.store.dispatch(new AddToStock(useProduct, false));
        }
      });
  }

  addToCart(event: MouseEvent): void {
    if (this.isInCart()(this.element.id)) {
      return;
    }

    this.cartAnimation.animateToCart(event.currentTarget);
    this.store
      .dispatch(
        new AddToCart({
          productId: this.element.id,
          name: this.element.name,
          articleNo: this.element.articleNo ?? '',
          quantityType: this.element.quantityType,
          quantity: 1,
          schematicImage: this.element.schematicImage,
        })
      )
      .subscribe();
  }

  removeFromCart(): void {
    this.store.dispatch(new RemoveFromCart(this.element.id));
  }

  openTransferDialog(): void {
    const config = createProductTransferDialogConfig(this.element, this.currentDepartmentId());
    const dialogRef = this.dialog.open<
      TransferProductModalComponent,
      TransferProductModalData,
      ITransferProductRequest
    >(TransferProductModalComponent, config);

    dialogRef.afterClosed().subscribe((transferRequest) => {
      if (transferRequest) {
        this.store.dispatch(new TransferProduct(transferRequest, config.data.sourceDepartmentId));
      }
    });
  }
}
